import React, { useEffect, useMemo, useState } from "react";
import {
  Dropdown,
  Image,
  Menu,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../../../store";
import axiosInstance from "../../../../helpers/axios";
import {
  deleteClientBillingAddressAPI,
  deleteClientDeliveryAddressAPI, // ✅ Import Delivery Delete API
  deleteClientPaymentMilestoneAPI,
  getClientBillingAddressListAPI,
  getClientDeliveryAddressListAPI, // ✅ Import Delivery Fetch API
  getClientsListAPI,
  getPaymentMilestonesListAPI,
  getCustomerAdditionalContactsAPI,
  deleteCustomerAdditionalContactAPI,
} from "../clientDistributorsApis";
import {
  decryptByText,
  getDecryptedName,
  isAdminSubAdminLoggedIn,
  responseDataFormate,
} from "../../../../helpers/bycrypt";
import { CreateClientModal } from "./CreateClientModal";
import { ModifyPreferencesModal } from "./ModifyPreferencesModal";
import { AddPaymentMilestoneModal } from "./AddPaymentMilestoneModal";
import { svg } from "../../../../Assets/svg";
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";
import { ModifyBillingAddressModal } from "./ModifyBillingAddressModal";
import { ModifyDeliveryAddressModal } from "./ModifyDeliveryAddressModal";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../../helpers/permissionModule";
import {
  CLIENT_FILTER_KEY,
  CLIENT_REFETCH_KEY,
  CLIENT_SCROLL_KEY,
  getVatValue,
  VatType,
} from "../clientDistributorTypes";
import {
  formatDeliveryAddress,
  getFullAwsUrl,
  getStaticSalespersonLabel,
} from "../../../../helpers/utils";
import { RoleTypesEnum } from "../../RolesAndPermissions/rolesAndPermissionsTypes";
import ClientAdditionalContactsFormModal from "./ClientAdditionalContactsFormModal";
// ✅ Import Delivery Modal (assuming you create a separate file or reuse ModifyBillingAddressModal with props)
// import { ModifyDeliveryAddressModal } from "./ModifyDeliveryAddressModal";

const BasicClientDetails: React.FC<{
  clientId: string;
  clientDetails: any;
  fetchClientDetails: () => void;
}> = ({ clientId, clientDetails, fetchClientDetails }) => {
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.CLIENTS_DISTRIBUTORS,
  );
  console.log(clientDetails, "clientDetailsclientDetails");
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const orderId = searchParams.get("order_id");
  const contextualParams = quoteId
    ? { quote_id: quoteId }
    : orderId
    ? { order_id: orderId }
    : undefined;

  const [orderData, setOrderData] = useState<any>(null);
  const [orderLoading, setOrderLoading] = useState(false);

  const isObjectId = (id: any) => /^[0-9a-fA-F]{24}$/.test(id);
  const displayOrderId =
    orderData?.order_id && !isObjectId(orderData?.order_id)
      ? orderData?.order_id
      : orderData?.order_id_num ||
        orderData?.display_quote_id ||
        orderData?.order_id ||
        orderId;

  const fetchOrderDetails = () => {
    if (!orderId) return;
    setOrderLoading(true);
    axiosInstance
      .get(`/orders/${orderId}`)
      .then((res: any) => {
        const result = responseDataFormate(res.data);
        if (result?.success) {
          setOrderData(result.data);
        }
      })
      .catch((err: any) => {
        console.error("Error fetching order details for client view:", err);
      })
      .finally(() => {
        setOrderLoading(false);
      });
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  // --- Loading States ---
  const [basicDetailsLoading, setBasicDetailsLoading] = useState(false);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [billingAddressLoading, setBillingAddressLoading] = useState(false);
  const [deliveryAddressLoading, setDeliveryAddressLoading] = useState(false); // ✅ New state

  // --- Data States ---
  // const [clientDetails, setClientDetails] = useState<any>(null);
  const [paymentMilestones, setPaymentMilestones] = useState<any[]>([]);
  const [billingAddressList, setBillingAddressList] = useState<any[]>([]);
  const [deliveryAddressList, setDeliveryAddressList] = useState<any[]>([]); // ✅ New state
  const [additionalContactsList, setAdditionalContactsList] = useState<any[]>(
    [],
  );
  const [additionalContactsLoading, setAdditionalContactsLoading] =
    useState(false);
  const [contactSearch, setContactSearch] = useState("");

  // --- Modal Visibility States ---
  const [isClientDetailsOpen, setIsClientDetailsOpen] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [isAddMilestoneOpen, setIsAddMilestoneOpen] = useState(false);
  const [isBillingAddressOpen, setIsBillingAddressOpen] = useState(false);
  const [isDeliveryAddressOpen, setIsDeliveryAddressOpen] = useState(false); // ✅ New state

  // --- Edit Data States ---
  const [editMilestoneData, setEditMilestoneData] = useState<any>(null);
  const [editAddressData, setEditAddressData] = useState<any>(null); // For Billing
  const [editDeliveryAddressData, setEditDeliveryAddressData] =
    useState<any>(null); // ✅ For Delivery

  // --- Delete States ---
  const [milestoneDeleting, setMilestoneDeleting] = useState(false);
  const [milestoneIdToDelete, setMilestoneIdToDelete] = useState<string | null>(
    null,
  );

  const [billingAddressDeleting, setBillingAddressDeleting] = useState(false);
  const [billingAddressIdToDelete, setBillingAddressIdToDelete] = useState<
    string | null
  >(null);

  const [deliveryAddressDeleting, setDeliveryAddressDeleting] = useState(false); // ✅ New state
  const [deliveryAddressIdToDelete, setDeliveryAddressIdToDelete] = useState<
    string | null
  >(null); // ✅ New state

  const [isAdditionalContactFormOpen, setIsAdditionalContactFormOpen] =
    useState(false);

  const [selectedAdditionalContact, setSelectedAdditionalContact] =
    useState<any>(null);

  const [additionalContactDeleting, setAdditionalContactDeleting] =
    useState(false);
  const [additionalContactIdToDelete, setAdditionalContactIdToDelete] =
    useState<string | null>(null);

  const fetchAdditionalContacts = () => {
    setAdditionalContactsLoading(true);
    dispatch(
      getCustomerAdditionalContactsAPI({
        id: clientId,
        onSuccess: (data: any) => {
          // Assuming the API returns the array or an object with additional_contacts array
          const list =
            data?.additional_contacts ||
            data?.additional_contact ||
            (Array.isArray(data) ? data : []);
          setAdditionalContactsList(list);
          setAdditionalContactsLoading(false);
        },
        onError: () => setAdditionalContactsLoading(false),
      }),
    );
  };

  const fetchClientPaymeentMilestones = () => {
    setMilestonesLoading(true);
    dispatch(
      getPaymentMilestonesListAPI({
        filters: { customer_id: clientId },
        onSuccess: (data: any) => {
          const newData = data?.data || [];
          setPaymentMilestones(newData);
          setMilestonesLoading(false);
        },
        onError: () => setMilestonesLoading(false),
      }),
    );
  };

  const fetchClientBillingAddress = () => {
    setBillingAddressLoading(true);
    dispatch(
      getClientBillingAddressListAPI({
        filters: { customer_id: clientId },
        onSuccess: (data: any) => {
          const newData = data?.data || [];
          setBillingAddressList(newData);
          setBillingAddressLoading(false);
        },
        onError: () => setBillingAddressLoading(false),
      }),
    );
  };

  // ✅ New Fetch Function
  const fetchClientDeliveryAddress = () => {
    setDeliveryAddressLoading(true);
    dispatch(
      getClientDeliveryAddressListAPI({
        filters: { customer_id: clientId },
        onSuccess: (data: any) => {
          const newData = data?.data || [];
          setDeliveryAddressList(newData);
          setDeliveryAddressLoading(false);
        },
        onError: () => setDeliveryAddressLoading(false),
      }),
    );
  };

  useEffect(() => {
    if (!clientId) return;
    fetchClientPaymeentMilestones();
    fetchClientBillingAddress();
    fetchClientDeliveryAddress(); // ✅ Call new fetcher
    fetchAdditionalContacts();
  }, [clientId]);

  // --- Helpers ---
  const renderDetailItem = (label: string, value: string | number | null) => (
    <div className="col-lg-3 col-md-4 col-sm-6">
      <label className="fs-6 fw-normal text-949494 d-block">{label}</label>
      <span className="fw-semibold text-primary fs-6">
        {value !== null && value !== undefined && value !== "" ? value : "-"}
      </span>
    </div>
  );

  const formatAddress = (loc: any) => {
    if (!loc) return "";
    const parts = [
      loc.address,
      loc.city,
      loc.state,
      loc?.country?.name ||
        (typeof loc?.country === "string" ? loc?.country : "") ||
        "",
      loc.zipcode || loc.zip,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // --- Milestone Logic ---
  const MilestoneMenu = (item: any) => (
    <Menu>
      {/* ✅ Modify Guard */}
      {USER_PERMISSIONS.MODIFY && (
        <Menu.Item key="edit" onClick={() => handleEditMilestone(item)}>
          <div className="d-flex align-items-center gap-2">Modify</div>
        </Menu.Item>
      )}
      {/* ✅ Remove Guard */}
      {USER_PERMISSIONS.REMOVE && (
        <Menu.Item
          key="delete"
          danger
          onClick={() => setMilestoneIdToDelete(item?._id)}
        >
          <div className="d-flex align-items-center gap-2">Remove</div>
        </Menu.Item>
      )}
    </Menu>
  );

  const handleEditMilestone = (milestone: any) => {
    setEditMilestoneData(milestone);
    setIsAddMilestoneOpen(true);
  };

  const handleCloseMilestoneModal = () => {
    setIsAddMilestoneOpen(false);
    setEditMilestoneData(null);
  };

  const handleDeleteMilestoneConfirm = () => {
    if (!milestoneIdToDelete) return;
    setMilestoneDeleting(true);
    dispatch(
      deleteClientPaymentMilestoneAPI({
        id: milestoneIdToDelete,
        params: contextualParams,
        onSuccess: () => {
          sessionStorage.removeItem(CLIENT_FILTER_KEY);
          sessionStorage.removeItem(CLIENT_SCROLL_KEY);
          sessionStorage.setItem(CLIENT_REFETCH_KEY, "1");
          setMilestoneDeleting(false);
          setPaymentMilestones((prev) =>
            prev.filter((p) => p._id !== milestoneIdToDelete),
          );
          setMilestoneIdToDelete(null);
        },
        onError: () => setMilestoneDeleting(false),
      }),
    );
  };

  // --- Billing Address Logic ---
  const AddressMenu = (item: any) => (
    <Menu>
      {USER_PERMISSIONS.MODIFY && (
        <Menu.Item key="edit" onClick={() => handleEditAddress(item)}>
          <div className="d-flex align-items-center gap-2">Modify</div>
        </Menu.Item>
      )}
      {USER_PERMISSIONS.REMOVE && !orderId && (
        <Menu.Item
          key="delete"
          danger
          onClick={() => setBillingAddressIdToDelete(item?._id)}
        >
          <div className="d-flex align-items-center gap-2">Remove</div>
        </Menu.Item>
      )}
    </Menu>
  );

  const handleEditAddress = (address: any) => {
    setEditAddressData(address);
    setIsBillingAddressOpen(true);
  };

  const handleDeleteBillingAddressConfirm = () => {
    if (!billingAddressIdToDelete) return;
    setBillingAddressDeleting(true);
    dispatch(
      deleteClientBillingAddressAPI({
        id: billingAddressIdToDelete,
        params: contextualParams,
        onSuccess: () => {
          setBillingAddressDeleting(false);
          setBillingAddressList((prev) =>
            prev.filter((p) => p._id !== billingAddressIdToDelete),
          );
          setBillingAddressIdToDelete(null);
        },
        onError: () => setBillingAddressDeleting(false),
      }),
    );
  };

  // --- Delivery Address Logic (New) ---

  const DeliveryAddressMenu = (item: any) => (
    <Menu>
      {USER_PERMISSIONS.MODIFY && (
        <Menu.Item key="edit" onClick={() => handleEditDeliveryAddress(item)}>
          <div className="d-flex align-items-center gap-2">Modify</div>
        </Menu.Item>
      )}
      {USER_PERMISSIONS.REMOVE && !orderId && (
        <Menu.Item
          key="delete"
          danger
          onClick={() => setDeliveryAddressIdToDelete(item?._id)}
        >
          <div className="d-flex align-items-center gap-2">Remove</div>
        </Menu.Item>
      )}
    </Menu>
  );

  const handleEditDeliveryAddress = (address: any) => {
    setEditDeliveryAddressData(address);
    setIsDeliveryAddressOpen(true);
  };

  const handleDeleteDeliveryAddressConfirm = () => {
    if (!deliveryAddressIdToDelete) return;
    setDeliveryAddressDeleting(true);
    dispatch(
      deleteClientDeliveryAddressAPI({
        id: deliveryAddressIdToDelete,
        params: contextualParams,
        onSuccess: () => {
          setDeliveryAddressDeleting(false);
          setDeliveryAddressList((prev) =>
            prev.filter((p) => p._id !== deliveryAddressIdToDelete),
          );
          setDeliveryAddressIdToDelete(null);
        },
        onError: () => setDeliveryAddressDeleting(false),
      }),
    );
  };

  // --- Additional Contacts Logic ---
  const handleEditAdditionalContact = (contact: any) => {
    setSelectedAdditionalContact(contact);
    setIsAdditionalContactFormOpen(true);
  };

  const AdditionalContactMenu = (item: any) => {
    return (
      <Menu>
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="edit"
            onClick={() => handleEditAdditionalContact(item)}
          >
            <div className="d-flex align-items-center gap-2">Modify</div>
          </Menu.Item>
        )}
        {USER_PERMISSIONS.REMOVE && (
          <Menu.Item
            key="delete"
            danger
            onClick={() => setAdditionalContactIdToDelete(item?._id)}
          >
            <div className="d-flex align-items-center gap-2">Remove</div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const handleDeleteAdditionalContactConfirm = () => {
    if (!additionalContactIdToDelete) return;
    setAdditionalContactDeleting(true);
    dispatch(
      deleteCustomerAdditionalContactAPI({
        payload: {
          customer_id: clientId,
          contact_id: additionalContactIdToDelete,
        },
        params: contextualParams,
        onSuccess: () => {
          setAdditionalContactDeleting(false);
          setAdditionalContactsList((prev) =>
            prev.filter((c) => c._id !== additionalContactIdToDelete),
          );
          setAdditionalContactIdToDelete(null);
        },
        onError: () => setAdditionalContactDeleting(false),
      }),
    );
  };

  const additionalContactColumns: TableColumnsType<any> = [
    {
      title: "",
      dataIndex: "is_sage_synced",
      key: "is_sage_synced",
      align: "center",
      render: (is_sage_synced: any, record: any) => {
        return (
          <Tooltip title={is_sage_synced ? "Data Synced" : "Data Not Synced"}>
            <span className="d-flex align-items-center justify-content-center svg-20 cursor-pointer">
              {is_sage_synced ? svg?.sageIconSync : svg?.sageNonSyncIcon}
            </span>
          </Tooltip>
        );
      },
      width: 50,
    },
    {
      title: "Full Name",
      key: "full_name",
      render: (_, record) => {
        const fName = decryptByText(record.first_name) || "";
        const lName = decryptByText(record.last_name) || "";
        const fullName = `${fName} ${lName}`.trim();
        return (
          <div className="d-flex align-items-center flex-wrap">
            <span>{fullName || "-"}</span>
            {orderId && displayOrderId && (
              <span
                className="text-secondary fw-semibold ms-2"
                style={{ fontSize: "13px" }}
              >
                (For order #{displayOrderId})
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (val) => decryptByText(val) || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (val) => val || "-",
    },
    {
      title: "Contact Type",
      key: "contact_type",
      render: (_, record) => {
        const isDefaultContact = record?.default_contact ?? false;
        const isDefaultAccountContact =
          record?.default_account_contact ?? false;

        if (!isDefaultContact && !isDefaultAccountContact) {
          return "-";
        }

        return (
          <div className="d-flex align-items-center flex-wrap gap-2">
            {isDefaultContact && (
              <span className="event-tag default-contact-tag">
                Default Contact
              </span>
            )}
            {isDefaultAccountContact && (
              <span className="event-tag default-account-tag">
                Default Account Contact
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      align: "center",
      render: (_, record) => {
        return (
          <div
            className="d-flex justify-content-center"
            onClick={(e) => e.stopPropagation()}
          >
            {(USER_PERMISSIONS.MODIFY || USER_PERMISSIONS.REMOVE) && (
              <Dropdown
                overlay={AdditionalContactMenu(record)}
                trigger={["click"]}
                placement="bottomRight"
                className="cursor-pointer"
              >
                <span className="svg-32 svg-outline-primary" role="button">
                  {svg?.tableMoreOption}
                </span>
              </Dropdown>
            )}
          </div>
        );
      },
    },
  ];

  const selectedBillingAddressId =
    orderData?.customer_snapshot?.billing_address?.address_id ||
    orderData?.customer_snapshot?.billing_address?._id ||
    orderData?.billing_address?.address_id ||
    orderData?.billing_address?._id;

  const isBillingAddressMatch = (item: any) => {
    if (selectedBillingAddressId) {
      if (String(item?._id) === String(selectedBillingAddressId)) return true;
    }
    const snap = orderData?.customer_snapshot?.billing_address;
    if (snap && snap.address && item?.address) {
      if (
        item.address.trim().toLowerCase() ===
          snap.address.trim().toLowerCase() &&
        (item.zipcode || item.zip || "") === (snap.zipcode || snap.zip || "")
      ) {
        return true;
      }
    }
    return false;
  };

  const displayedBillingAddressList = useMemo(() => {
    if (!orderId || !orderData) return billingAddressList;
    const filtered = billingAddressList.filter(isBillingAddressMatch);
    if (filtered.length > 0) return filtered;
    const snap = orderData?.customer_snapshot?.billing_address;
    if (snap && (snap.address || snap._id || snap.address_id)) {
      return [
        {
          _id: snap._id || snap.address_id || "snap_billing",
          ...snap,
        },
      ];
    }
    return [];
  }, [orderId, orderData, billingAddressList]);

  const selectedDeliveryAddressId =
    orderData?.customer_snapshot?.delivery_address?.address_id ||
    orderData?.customer_snapshot?.delivery_address?._id ||
    orderData?.delivery?.address_id ||
    orderData?.delivery?.delivery_address?._id;

  const isDeliveryAddressMatch = (item: any) => {
    if (selectedDeliveryAddressId) {
      if (String(item?._id) === String(selectedDeliveryAddressId)) return true;
    }
    const snap =
      orderData?.customer_snapshot?.delivery_address ||
      orderData?.delivery?.delivery_address;
    if (snap && snap.address && item?.address) {
      if (
        item.address.trim().toLowerCase() ===
          snap.address.trim().toLowerCase() &&
        (item.zipcode || item.zip || "") === (snap.zipcode || snap.zip || "")
      ) {
        return true;
      }
    }
    return false;
  };

  const displayedDeliveryAddressList = useMemo(() => {
    if (!orderId || !orderData) return deliveryAddressList;
    const filtered = deliveryAddressList.filter(isDeliveryAddressMatch);
    if (filtered.length > 0) return filtered;
    const snap =
      orderData?.customer_snapshot?.delivery_address ||
      orderData?.delivery?.delivery_address;
    if (snap && (snap.address || snap._id || snap.address_id)) {
      return [
        {
          _id: snap._id || snap.address_id || "snap_delivery",
          ...snap,
        },
      ];
    }
    return [];
  }, [orderId, orderData, deliveryAddressList]);

  const orderContactIds = useMemo(() => {
    if (!orderData) return [];
    const ids = [
      orderData?.customer_snapshot?.additional_contact_id,
      orderData?.customer_snapshot?.additional_account_contact_id,
      orderData?.additional_contact_id,
      orderData?.additional_account_contact_id,
    ]
      .filter(Boolean)
      .map((id: any) =>
        String(typeof id === "object" && id?._id ? id._id : id),
      );
    return Array.from(new Set(ids));
  }, [orderData]);

  const displayedAdditionalContactsList = useMemo(() => {
    if (!orderId || !orderData) return additionalContactsList;
    if (orderContactIds.length === 0) return [];
    return additionalContactsList.filter((c: any) =>
      orderContactIds.includes(String(c?._id)),
    );
  }, [orderId, orderData, orderContactIds, additionalContactsList]);

  const filteredContacts = displayedAdditionalContactsList.filter(
    (contact: any) => {
      if (!contactSearch) return true;
      const term = contactSearch.toLowerCase();

      const fName = decryptByText(contact.first_name) || "";
      const lName = decryptByText(contact.last_name) || "";
      const fullName = `${fName} ${lName}`.trim().toLowerCase();

      const email = decryptByText(contact.email) || "";
      const lowerEmail = email.toLowerCase();

      const phone = (contact.phone || "").toLowerCase();
      const department = (contact.department || "").toLowerCase();

      return (
        fullName.includes(term) ||
        lowerEmail.includes(term) ||
        phone.includes(term) ||
        department.includes(term)
      );
    },
  );

  return (
    <>
      {/* Basic Details Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Basic Details</h4>
        {USER_PERMISSIONS.MODIFY && (
          <div
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setIsClientDetailsOpen(true);
            }}
          >
            Modify
          </div>
        )}
      </div>
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        {basicDetailsLoading ? (
          <div className="text-center py-5">
            <Spin size="large" />
          </div>
        ) : clientDetails ? (
          <div className="row gy-3">
            {renderDetailItem("Sage Ref", clientDetails?.sage_reference)}
            {renderDetailItem("Company Name", clientDetails?.company_name)}
            {renderDetailItem(
              "Type",
              clientDetails?.role_data?.name ||
                (clientDetails?.role_data?.key === "distributor"
                  ? "Distributor"
                  : "Client"),
            )}
            {renderDetailItem(
              "Customer Type",
              clientDetails?.customer_type === "new"
                ? "New Customer"
                : clientDetails?.customer_type === "regular"
                ? "Regular Customer"
                : "New Customer",
            )}
            {renderDetailItem(
              "Contact Person",
              getDecryptedName(clientDetails),
            )}
            {renderDetailItem("Phone", clientDetails?.phone)}
            {renderDetailItem(
              "Customer Email",
              clientDetails?.email ? decryptByText(clientDetails.email) : "-",
            )}
            {/* {renderDetailItem(
              "Sage Account Contact Email",
              clientDetails?.sage_account_contact_email
                ? decryptByText(clientDetails.sage_account_contact_email)
                : "-",
            )} */}
            {renderDetailItem("VAT Type", getVatValue(clientDetails?.vat_type))}
            {clientDetails?.vat_type === VatType.VAT_REGISTERED &&
              renderDetailItem("VAT Number", clientDetails?.vat)}
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                {" "}
                VAT Percentage
              </label>
              <span className="fw-semibold text-primary fs-6">
                {clientDetails?.tax_id?.name ? (
                  <>
                    {clientDetails?.tax_id?.name
                      ? clientDetails?.tax_id?.name
                      : ""}{" "}
                    {`(${
                      clientDetails?.tax_id?.tax_rate !== undefined &&
                      clientDetails?.tax_id?.tax_rate !== null
                        ? `${clientDetails?.tax_id?.tax_rate}%`
                        : ""
                    })`}{" "}
                    {`(${
                      clientDetails?.tax_id?.code
                        ? `Code : ${clientDetails?.tax_id?.code}`
                        : ""
                    })`}
                  </>
                ) : clientDetails?.vat_percentage !== undefined &&
                  clientDetails?.vat_percentage !== null ? (
                  `${clientDetails.vat_percentage}%`
                ) : (
                  "-"
                )}
              </span>
            </div>

            {renderDetailItem("Currency", clientDetails?.currency || "GBP")}
            {renderDetailItem(
              "Salesperson",
              getStaticSalespersonLabel(clientDetails?.sales_person) ||
                (clientDetails?.sales_person
                  ? getDecryptedName(clientDetails.sales_person)
                  : "-"),
            )}
            <div className="col-lg-3 col-md-4 col-sm-6">
              <label className="fs-6 fw-normal text-949494 d-block">
                Company Logo
              </label>
              {clientDetails?.client_load_notice_logo ? (
                <div className="d-flex align-items-center gap-3 mt-1">
                  <Image
                    src={
                      clientDetails?.client_load_notice_logo
                        ? getFullAwsUrl(clientDetails?.client_load_notice_logo)
                        : ""
                    }
                    alt="Company Load Notice Logo"
                    className="object-fit-contain"
                    style={{ height: "60px" }}
                  />
                </div>
              ) : (
                <div>-</div>
              )}
            </div>
            {clientDetails?.role_data?.key === "distributor" &&
              renderDetailItem(
                "Phone Number for LN",
                clientDetails?.phone_for_ln ? clientDetails.phone_for_ln : "-",
              )}
            {clientDetails?.role_data?.key === "distributor" &&
              renderDetailItem(
                "Website for LN",
                clientDetails?.website ? clientDetails.website : "-",
              )}
            {renderDetailItem(
              "Show Retail Price",
              clientDetails?.show_retail_price ? "Yes" : "No",
            )}
          </div>
        ) : (
          <div className="text-center text-secondary py-3">
            No details available.
          </div>
        )}
      </div>

      <div className="d-flex flex-row flex-wrap gap-2 justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Additional Contacts</h4>
        {USER_PERMISSIONS.MODIFY && (
          <div
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer order-md-1"
            onClick={(e) => {
              setIsAdditionalContactFormOpen(true);
              setSelectedAdditionalContact(null);
            }}
          >
            Add
          </div>
        )}
        <div className="pageSearch mw-100 position-relative d-inline-block ms-md-auto">
          <input
            type="text"
            value={contactSearch}
            className="form-control form-control-lg"
            onChange={(e) => setContactSearch(e.target.value)}
            placeholder="Search"
          />
          {contactSearch?.length ? (
            <span
              className="cursor-pointer"
              onClick={() => setContactSearch("")}
            >
              {svg?.searchClear}
            </span>
          ) : (
            <span>{svg?.search}</span>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 bg-white mb-4 overflow-hidden">
        {additionalContactsLoading || (orderId && orderLoading) ? (
          <div className="text-center py-5">
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={additionalContactColumns}
            dataSource={filteredContacts}
            rowKey="_id"
            pagination={false}
            scroll={{ x: 800 }}
            locale={{ emptyText: "No Additional Contacts Added Yet." }}
          />
        )}
      </div>

      {/* Payment Milestones Section */}
      {/* <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Payment Milestones</h4>
        {USER_PERMISSIONS.ADD && isAdminSubAdminLoggedIn() && (
          <div
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setEditMilestoneData(null);
              setIsAddMilestoneOpen(true);
            }}
          >
            Add
          </div>
        )}
      </div>
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        {milestonesLoading ? (
          <div className="text-center py-5">
            <Spin size="large" />
          </div>
        ) : paymentMilestones.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {paymentMilestones.map((item: any) => {
              const isPercentage = item.value_base === "percentage";
              return (
                <div
                  key={item._id}
                  className="d-flex justify-content-between align-items-center border rounded-3 px-3 py-3 bg-white"
                >
                  <div className="fw-semibold text-dark fs-6">
                    {item.milestone_number}. {item.milestone_name} -{" "}
                    {isPercentage ? `${item.value}%` : `£${item.value}`}
                  </div>
                  {isAdminSubAdminLoggedIn() && (
                    <>
                      {(USER_PERMISSIONS.MODIFY || USER_PERMISSIONS.REMOVE) && (
                        <Dropdown
                          overlay={MilestoneMenu(item)}
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <span className="cursor-pointer d-flex align-items-center justify-content-center svg-24 text-dark">
                            {svg?.tableMoreOption}
                          </span>
                        </Dropdown>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-secondary py-3">
            No Payment Milestones Added Yet.
          </div>
        )}
      </div> */}

      {/* Preferences Section */}
      {/* <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Preferences</h4>
        {USER_PERMISSIONS.MODIFY && (
          <div
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setIsPreferencesOpen(true);
            }}
          >
            Modify
          </div>
        )}
      </div>
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        {basicDetailsLoading ? (
          <div className="text-center py-5">
            <Spin size="large" />
          </div>
        ) : clientDetails ? (
          <div className="row gy-3">
            {renderDetailItem(
              "Color Preference",
              clientDetails?.preferences?.color,
            )}
            {renderDetailItem(
              "Include Design Preview",
              clientDetails?.preferences?.design_preview ? "Yes" : "No",
            )}
          </div>
        ) : (
          <div className="text-center text-secondary py-3">
            No Preferences Selected Yet!
          </div>
        )}
      </div> */}
      {/* Billing Address Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Billing Address</h4>
        {USER_PERMISSIONS.ADD && !orderId && (
          <div
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setEditAddressData(null);
              setIsBillingAddressOpen(true);
            }}
          >
            + Add
          </div>
        )}
      </div>
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        {billingAddressLoading || (orderId && orderLoading) ? (
          <div className="text-center py-5">
            <Spin size="large" />
          </div>
        ) : displayedBillingAddressList.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {displayedBillingAddressList.map((item: any) => (
              <div
                key={item._id}
                className="d-flex justify-content-between align-items-center border rounded-3 px-3 py-3 bg-white"
              >
                <div className="fw-semibold text-dark fs-6 d-flex align-items-center flex-wrap gap-2">
                  <span>{formatAddress(item)}</span>
                  {orderId && displayOrderId && (
                    <span
                      className="text-secondary fw-semibold"
                      style={{ fontSize: "13px" }}
                    >
                      (For order #{displayOrderId})
                    </span>
                  )}
                </div>
                {(USER_PERMISSIONS.MODIFY ||
                  (USER_PERMISSIONS.REMOVE && !orderId)) && (
                  <Dropdown
                    overlay={AddressMenu(item)}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <span className="cursor-pointer d-flex align-items-center justify-content-center svg-24 text-dark">
                      {svg?.tableMoreOption}
                    </span>
                  </Dropdown>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-secondary py-3">
            No Billing Address Added Yet.
          </div>
        )}
      </div>
      {/* ✅ Delivery Address Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fs-4 fw-bold text-primary mb-0">Delivery Address</h4>
        {USER_PERMISSIONS.ADD && !orderId && (
          <div
            className="fs-6 fw-bold text-decoration-underline text-primary cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setEditDeliveryAddressData(null);
              setIsDeliveryAddressOpen(true);
            }}
          >
            + Add
          </div>
        )}
      </div>
      <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
        {deliveryAddressLoading || (orderId && orderLoading) ? (
          <div className="text-center py-5">
            <Spin size="large" />
          </div>
        ) : displayedDeliveryAddressList.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {displayedDeliveryAddressList.map((item: any) => (
              <div
                key={item._id}
                className="d-flex justify-content-between align-items-center border rounded-3 px-3 py-3 bg-white"
              >
                <div className="fw-semibold text-dark fs-6 d-flex align-items-center flex-wrap gap-2">
                  <span className="svg-20">
                    {item?.is_sage_synced
                      ? svg?.sageIconSync
                      : svg?.sageNonSyncIcon}
                  </span>{" "}
                  <span>{formatDeliveryAddress(item)}</span>
                  {orderId && displayOrderId && (
                    <span
                      className="text-secondary fw-semibold"
                      style={{ fontSize: "13px" }}
                    >
                      (For order #{displayOrderId})
                    </span>
                  )}
                </div>
                {(USER_PERMISSIONS.MODIFY ||
                  (USER_PERMISSIONS.REMOVE && !orderId)) && (
                  <Dropdown
                    overlay={DeliveryAddressMenu(item)}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <span className="cursor-pointer d-flex align-items-center justify-content-center svg-24 text-dark">
                      {svg?.tableMoreOption}
                    </span>
                  </Dropdown>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-secondary py-3">
            No Delivery Address Added Yet.
          </div>
        )}
      </div>
      {/* --- Modals --- */}
      {isClientDetailsOpen && (
        <CreateClientModal
          open={isClientDetailsOpen}
          onClose={() => setIsClientDetailsOpen(false)}
          editData={clientDetails}
          refreshData={() => {
            fetchClientDetails();
            if (orderId) fetchOrderDetails();
          }}
        />
      )}
      {isPreferencesOpen && (
        <ModifyPreferencesModal
          open={isPreferencesOpen}
          onClose={() => setIsPreferencesOpen(false)}
          clientId={clientId}
          preferencesData={clientDetails?.preferences}
          refreshData={() => {
            fetchClientDetails();
            if (orderId) fetchOrderDetails();
          }}
        />
      )}
      {isAddMilestoneOpen && (
        <AddPaymentMilestoneModal
          open={isAddMilestoneOpen}
          onClose={handleCloseMilestoneModal}
          clientId={clientId}
          refreshData={() => {
            fetchClientPaymeentMilestones();
            if (orderId) fetchOrderDetails();
          }}
          editData={editMilestoneData}
        />
      )}
      {isBillingAddressOpen && (
        <ModifyBillingAddressModal
          open={isBillingAddressOpen}
          onClose={() => setIsBillingAddressOpen(false)}
          clientId={clientId}
          refreshData={(newAddress?: any) => {
            fetchClientBillingAddress();
            if (orderId) fetchOrderDetails();
          }}
          addressData={editAddressData}
        />
      )}
      {isDeliveryAddressOpen && (
        <ModifyDeliveryAddressModal
          open={isDeliveryAddressOpen}
          onClose={() => setIsDeliveryAddressOpen(false)}
          clientId={clientId}
          refreshData={(newAddress?: any) => {
            fetchClientDeliveryAddress();
            if (orderId) fetchOrderDetails();
          }}
          addressData={editDeliveryAddressData}
          isDistributor={
            clientDetails?.role_data?.key === RoleTypesEnum.DISTRIBUTOR
          }
        />
      )}
      {/* --- Delete Confirmation Modals --- */}
      <CommonConfirmationModal
        open={milestoneIdToDelete != null}
        title="Remove Payment Milestones, Are You Sure?"
        subTitle="Removing payment milestones will delete all related milestone details from this project. Please confirm before proceeding."
        loading={milestoneDeleting}
        onSuccess={handleDeleteMilestoneConfirm}
        onClose={() => setMilestoneIdToDelete(null)}
      />
      <CommonConfirmationModal
        open={billingAddressIdToDelete != null}
        title="Remove Billing Address, Are You Sure?"
        subTitle="Removing the billing address will delete it from this record. Please confirm before proceeding."
        loading={billingAddressDeleting}
        onSuccess={handleDeleteBillingAddressConfirm}
        onClose={() => setBillingAddressIdToDelete(null)}
      />
      {/* ✅ Delivery Address Delete Modal */}
      <CommonConfirmationModal
        open={deliveryAddressIdToDelete != null}
        title="Remove Delivery Address, Are You Sure?"
        subTitle="Removing the delivery address will delete it from this record. Please confirm before proceeding."
        loading={deliveryAddressDeleting}
        onSuccess={handleDeleteDeliveryAddressConfirm}
        onClose={() => setDeliveryAddressIdToDelete(null)}
      />
      <CommonConfirmationModal
        open={additionalContactIdToDelete != null}
        title="Remove Additional Contact, Are You Sure?"
        subTitle="Removing this contact will delete it from this record. Please confirm before proceeding."
        loading={additionalContactDeleting}
        onSuccess={handleDeleteAdditionalContactConfirm}
        onClose={() => setAdditionalContactIdToDelete(null)}
      />

      <ClientAdditionalContactsFormModal
        open={isAdditionalContactFormOpen}
        onClose={() => setIsAdditionalContactFormOpen(false)}
        refreshData={() => {
          fetchAdditionalContacts();
          if (orderId) fetchOrderDetails();
        }}
        editData={selectedAdditionalContact}
        clientid={clientId}
        existingContacts={additionalContactsList}
      />
    </>
  );
};

export default BasicClientDetails;
